import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { BiLockAlt, BiX } from 'react-icons/bi'
import { FiInfo } from 'react-icons/fi'
import {
  ImCheckmark,
  ImCloudDownload,
  ImSpinner10,
  ImUpload
} from 'react-icons/im'
import { TbArrowMoveLeft, TbArrowMoveRight } from 'react-icons/tb'

// Formata una data (yyyy-mm-dd) a format europeu dd/mm/aaaa NOMÉS per mostrar-la.
// Les dades reals (row.student) no es toquen: el que s'envia al backend
// manté sempre el format original.
const formatDateDisplay = (value) => {
  if (!value) return value
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value))
  if (!match) return value
  const [, year, month, day] = match
  return `${day}/${month}/${year}`
}

// Mostra la data tal com l'ha escrit el centre a l'Excel.
// Si a la cel·la hi havia TEXT (rawValue és string), es mostra LITERALMENT el text (només es retallen espais/salts de línia sobrants), sense reordenar dia/mes.
// Si la cel·la era una data real d'Excel (objecte Date / número de sèrie), no hi ha
// ambigüitat possible i es mostra formatada en dd/mm/aaaa amb formatDateDisplay.
const formatDateCell = (rawValue, formattedValue) => {
  if (typeof rawValue === 'string') return rawValue.trim()
  return formatDateDisplay(formattedValue)
}

// Uneix una llista de valors en text llegible: "5", "5 i 8" o "5, 8 i 12"
const joinReadable = (values) => {
  if (values.length === 0) return ''
  if (values.length === 1) return String(values[0])
  return `${values.slice(0, -1).join(', ')} i ${values[values.length - 1]}`
}

// Missatges (un per origen) explicant, sense ambigüitat, ON s'ha trobat el duplicat:
// - si és dins del mateix Excel: a quina(es) fila(es)
// - si ja existia a l'aplicació: amb quin(s) codi(s) de becari
// Es retorna una llista perquè, quan coincideix a les dues bandes, es mostrin com dos avisos separats
// i no es puguin confondre en un de sol.
const duplicateSourceMessages = (row) => {
  const { duplicateExcelRows, duplicateCodisBecari } = row
  const messages = []

  if (duplicateExcelRows?.length > 0) {
    const fila = duplicateExcelRows.length === 1 ? 'fila' : 'files'
    messages.push({
      icon: '📄',
      text: `Aquest mateix alumne surt més d'una vegada al fitxer Excel que s'ha pujat, a la ${fila} ${joinReadable(duplicateExcelRows)}.`
    })
  }

  if (duplicateCodisBecari?.length > 0) {
    const codi =
      duplicateCodisBecari.length === 1 ? 'codi de becari' : 'codis de becari'
    messages.push({
      icon: '💾',
      text: `Aquest alumne ja estava donat d'alta en el programa abans de pujar l'Excel (${codi}: ${joinReadable(duplicateCodisBecari)}).`
    })
  }

  return messages
}

// Detall complet d'un conflicte concret (per al tooltip): explica, en una frase clara,
// amb quin període exacte xoca i on és
const formatConflictDetail = (conflict) => {
  const periode = `del ${formatDateDisplay(conflict.inici) || '—'} al ${formatDateDisplay(conflict.fi) || '—'}`
  if (conflict.source === 'app') {
    const codi = conflict.codiBecari
      ? `codi de becari ${conflict.codiBecari}`
      : 'codi de becari desconegut'
    return `Ja existia en el programa (${codi}${conflict.nom ? `, ${conflict.nom}` : ''}) amb pràctiques ${periode}`
  }
  return `A la fila ${conflict.rowNumber} del mateix Excel${conflict.nom ? ` (${conflict.nom})` : ''} amb pràctiques ${periode}`
}

// Modal amb stepper (3 passos) per a la importació d'Alumnes des d'Excel:
// Pas 1: Pujar/arrossegar fitxer
// Pas 2: Previsualització de files (colors segons validacions/solapaments)
// Pas 3: Resultat de la importació
export default function ImportAlumnesModal({
  onClose,
  handleAnalyzeExcel,
  handleConfirmImport
}) {
  // useState()
  const [step, setStep] = useState(1)
  const [file, setFile] = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const [loading, setLoading] = useState(false)
  const [analyzeError, setAnalyzeError] = useState(null)
  const [previewRows, setPreviewRows] = useState([])
  const [importResult, setImportResult] = useState(null)

  // Bloqueja el scroll del body mentre el modal està obert, i el restaura en tancar-lo
  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  // Guarda el fitxer pujat
  const uploadDocument = (e) => {
    setFile(e?.target?.files[0])
    setAnalyzeError(null)
  }

  // Permet recollir un fitxer arrossegat i deixat anar sobre el div
  const dropHandler = (ev) => {
    ev.preventDefault()

    if (ev.dataTransfer.items) {
      if (ev.dataTransfer.items.length > 1) {
        return
      }
      const item = ev.dataTransfer.items[0]
      if (item.kind === 'file') {
        setFile(item.getAsFile())
        setAnalyzeError(null)
      }
    } else {
      if (ev.dataTransfer.files.length > 1) {
        return
      }
      setFile(ev.dataTransfer.files[0])
      setAnalyzeError(null)
    }
  }

  const dragOverHandler = (ev) => {
    ev.preventDefault()
  }

  // Pas 1 -> Pas 2: analitza el fitxer Excel i genera la previsualització
  const handleGoToPreview = async () => {
    setLoading(true)
    setAnalyzeError(null)

    try {
      const rows = await handleAnalyzeExcel(file)
      setPreviewRows(rows)
      setStep(2)
    } catch (error) {
      setAnalyzeError(error?.message || "No s'ha pogut analitzar el fitxer.")
    } finally {
      setLoading(false)
    }
  }

  // Pas 2 -> Pas 3: importa les files seleccionades
  const handleGoToResult = async () => {
    const analyzedStudents = previewRows?.map((r) => r.student)
    setLoading(true)
    const result = await handleConfirmImport(analyzedStudents)
    setLoading(false)

    if (result) {
      setImportResult(result)
      setStep(3)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-black bg-base/75 bg-opacity-60"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-center h-screen min-h-screen px-4 pt-4 pb-4 text-center sm:items-end sm:block sm:p-0">
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div
          className={`relative inline-flex flex-col w-full h-full px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white border-2 border-indigo-800 rounded-sm shadow-xl sm:h-5/6 md:h-4/6 sm:align-middle sm:w-11/12 xl:w-10/12 2xl:w-9/12 lg:p-2 ${loading ? 'cursor-wait' : ''}`}
        >
          {/* Overlay que bloqueja clics i scroll mentre s'analitza o s'importa */}
          {loading && (
            <div
              className="absolute inset-0 z-50 cursor-wait"
              aria-hidden="true"
            />
          )}

          {/* Passos */}
          <ol className="flex items-center justify-between w-full px-8 pt-3 md:space-x-2 lg:space-x-8">
            {/* Pas 1: Centre + Fitxer */}
            <li
              className={`flex items-center space-x-2.5 ${step === 1 ? 'text-blue-600 animate-pulse' : 'text-green-500'}`}
            >
              {step > 1 ? (
                <span className="flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -start-4 ring-4 ring-white">
                  <ImCheckmark className="text-green-500" size={20} />
                </span>
              ) : (
                <span className="flex items-center justify-center w-8 h-8 border border-blue-600 rounded-full shrink-0">
                  1
                </span>
              )}

              <span
                className={`${step === 1 ? 'flex flex-col' : 'hidden md:flex md:flex-col'}`}
              >
                <h3 className="font-medium leading-tight">Afegir fitxer</h3>
                <p className="text-sm">Pujeu l&apos;Excel</p>
              </span>
            </li>

            {/* Pas 2: Previsualització */}
            <li
              className={`flex items-center space-x-2.5 ${step === 2 ? 'text-blue-600 animate-pulse' : step > 2 ? 'text-green-500' : 'text-gray-500'}`}
            >
              {step > 2 ? (
                <span className="flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -start-4 ring-4 ring-white">
                  <ImCheckmark className="text-green-500" size={20} />
                </span>
              ) : (
                <span
                  className={`flex items-center justify-center w-8 h-8 border rounded-full shrink-0 ${step === 2 ? 'border-blue-600' : 'border-gray-500'}`}
                >
                  2
                </span>
              )}

              <span
                className={`${step === 2 ? 'flex flex-col' : 'hidden md:flex md:flex-col'}`}
              >
                <h3 className="font-medium leading-tight">Anàlisi</h3>
                <p className="text-sm">Revisió automàtica</p>
              </span>
            </li>

            {/* Pas 3: Resultat */}
            <li
              className={`flex items-center space-x-2.5 ${step === 3 ? 'text-green-500 animate-pulse' : 'text-gray-500'}`}
            >
              <span
                className={`flex items-center justify-center w-8 h-8 border rounded-full shrink-0 ${step === 3 ? 'border-green-500' : 'border-gray-500'}`}
              >
                {step === 3 ? (
                  <ImCheckmark className="text-green-500" size={20} />
                ) : (
                  3
                )}
              </span>

              <span
                className={`${step === 3 ? 'flex flex-col' : 'hidden md:flex md:flex-col'}`}
              >
                <h3 className="font-medium leading-tight">Resultat</h3>
                <p className="text-sm">Resum</p>
              </span>
            </li>
          </ol>

          {/* Barra de progrés */}
          <div className="w-full my-5 bg-gray-200 rounded-full">
            <div
              className="text-xs font-medium text-white text-center p-0.5 leading-none rounded-full bg-blue-500"
              style={{
                width: step === 1 ? '33%' : step === 2 ? '66%' : '100%'
              }}
            />
          </div>

          {/* Contingut segons el pas */}
          {step === 1 && (
            <div className="flex flex-col w-full space-y-4 grow">
              <div
                id="drop_zone"
                className="flex flex-col items-center justify-center w-full gap-2 grow"
                onDrop={(e) => dropHandler(e)}
                onDragOver={(e) => {
                  setDragOver(true)
                  dragOverHandler(e)
                }}
                onDragLeave={() => setDragOver(false)}
              >
                <div className="flex items-center w-full gap-3 p-4 border border-blue-100 rounded-lg shadow bg-blue-50">
                  <FiInfo
                    className="flex-shrink-0 mt-0.5 text-blue-500"
                    size={20}
                  />

                  <div>
                    <p className="mb-1 text-sm font-semibold text-gray-800">
                      Abans d&apos;importar els alumnes
                    </p>

                    <p className="text-sm text-gray-600">
                      Cal seleccionar el fitxer Excel amb les dades dels alumnes
                      i prémer{' '}
                      <span className="font-semibold">«Analitzar fitxer»</span>.
                      El sistema revisarà automàticament la informació i
                      indicarà si hi ha algun error.
                      <span className="block mt-1">
                        Si no es detecten errors, es podrà continuar amb la
                        importació. Si es detecta algun error, caldrà revisar i
                        corregir les dades de l&apos;Excel i tornar a analitzar
                        el fitxer abans d&apos;importar-lo.
                      </span>
                    </p>
                  </div>
                </div>

                <label
                  htmlFor="dropzone-file-alumnes"
                  className={`flex flex-col items-center justify-center w-full h-full border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-100 ${dragOver ? 'bg-gray-100' : 'bg-gray-50'}`}
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <ImCloudDownload className="text-gray-500" size={30} />

                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">
                        Cal fer clic aquí per seleccionar un fitxer Excel
                      </span>{' '}
                      o arrossegar-lo fins aquí
                    </p>

                    <p className="mb-2 text-xs text-gray-500">
                      Només s&apos;admeten fitxers Excel (.xlsx)
                    </p>

                    <p className="text-xs text-gray-500">
                      <span className="font-semibold">
                        {file
                          ? `Fitxer seleccionat: ${file.name}`
                          : 'Encara no s’ha seleccionat cap fitxer'}
                      </span>
                    </p>
                  </div>

                  <input
                    id="dropzone-file-alumnes"
                    type="file"
                    className="hidden"
                    accept=".xlsx"
                    onChange={(e) => uploadDocument(e)}
                  />
                </label>
              </div>

              {analyzeError && (
                <div className="w-full p-3 text-sm text-left text-red-700 border-2 border-red-200 rounded-lg bg-red-50">
                  {analyzeError}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="flex flex-col w-full h-full overflow-hidden grow">
              {(() => {
                const overlapCount = previewRows.filter(
                  (r) => r.category === 'overlap'
                ).length
                const monthAdjacentCount = previewRows.filter(
                  (r) => r.category === 'monthAdjacent'
                ).length
                const errorCount = previewRows.filter(
                  (r) => r.errors.length > 0
                ).length

                if (
                  overlapCount === 0 &&
                  monthAdjacentCount === 0 &&
                  errorCount === 0
                )
                  return (
                    <p className="px-1 pb-3 text-sm font-medium text-green-700">
                      Totes les files són correctes i es poden importar.
                    </p>
                  )

                return (
                  <div className="flex flex-col gap-y-1.5 px-1 pb-3 mb-1 text-xs text-gray-600">
                    <div className="flex items-center min-h-[1.25rem] gap-1.5">
                      <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" />
                      <span className="whitespace-nowrap">
                        {overlapCount} alumne{overlapCount !== 1 ? 's' : ''} amb
                        DNI duplicat i amb període de pràctiques solapat
                      </span>
                    </div>

                    <div className="flex items-center min-h-[1.25rem] gap-1.5">
                      <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-400 shrink-0" />
                      <span className="whitespace-nowrap">
                        {monthAdjacentCount} alumne
                        {monthAdjacentCount !== 1 ? 's' : ''} amb DNI duplicat,
                        algun període de pràctiques acaba i comença en el mateix
                        mes
                      </span>
                    </div>

                    <div className="flex items-center min-h-[1.25rem] gap-1.5">
                      <span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-300 shrink-0" />
                      <span className="whitespace-nowrap">
                        {errorCount} alumne{errorCount !== 1 ? 's' : ''} amb
                        algún camp incorrecte
                      </span>
                    </div>
                  </div>
                )
              })()}

              <div className="h-full min-h-0 overflow-y-auto border-2 border-gray-300 rounded-md grow">
                <table className="w-full h-full text-sm border-separate table-fixed border-spacing-0">
                  <colgroup>
                    <col className="w-8" />
                    <col className="w-24" />
                    <col className="w-72" />
                    <col className="w-24" />
                    <col className="w-24" />
                    <col className="w-96 md:w-fit" />
                  </colgroup>

                  <thead>
                    <tr className="text-xs text-left text-gray-500">
                      <th className="sticky top-0 z-10 p-1.5 font-semibold text-center bg-gray-50 border-b border-r border-gray-300">
                        Fila
                      </th>

                      <th className="sticky top-0 z-10 p-1.5 font-semibold bg-gray-50 border-b border-r border-gray-300">
                        DNI NIE PASS
                      </th>

                      <th className="sticky top-0 z-10 p-1.5 font-semibold bg-gray-50 border-b border-r border-gray-300">
                        COGNOM COGNOM NOM
                      </th>

                      <th className="sticky top-0 z-10 p-1.5 font-semibold bg-gray-50 border-b border-r border-gray-300">
                        INICI PRACTIQUES
                      </th>

                      <th className="sticky top-0 z-10 p-1.5 font-semibold bg-gray-50 border-b border-r border-gray-300">
                        FI PRACTIQUES
                      </th>

                      <th className="sticky top-0 z-10 p-1.5 font-semibold border-b border-gray-300 bg-gray-50">
                        Estat
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {previewRows.map((row, i) => {
                      const hasErrors = row.errors.length > 0
                      const isOverlap = row.category === 'overlap'
                      const isMonthAdjacent = row.category === 'monthAdjacent'
                      const isLastRow = i === previewRows.length - 1
                      const borderB = isLastRow ? '' : 'border-b'

                      return (
                        <tr
                          key={i}
                          className={`align-top ${
                            isOverlap
                              ? 'bg-red-50/60'
                              : isMonthAdjacent
                                ? 'bg-amber-50/50'
                                : hasErrors
                                  ? 'bg-gray-50/60'
                                  : i % 2 === 1
                                    ? 'bg-gray-50/40'
                                    : ''
                          }`}
                        >
                          <td
                            className={`p-1.5 text-center text-gray-400 ${borderB} border-r border-gray-300`}
                          >
                            {row.excelRowNumber}
                          </td>

                          <td
                            className={`p-1.5 ${borderB} border-r border-gray-300 truncate ${isOverlap ? 'font-semibold text-red-600' : isMonthAdjacent ? 'font-semibold text-amber-600' : hasErrors ? 'text-gray-400' : 'text-gray-700'}`}
                          >
                            {row.student['DNI NIE PASS']}
                          </td>

                          <td
                            className={`p-1.5 ${borderB} border-r border-gray-300 truncate ${hasErrors ? 'text-gray-400' : 'text-gray-700'}`}
                          >
                            {row.student['COGNOM COGNOM NOM']}
                          </td>

                          <td
                            className={`p-1.5 ${borderB} border-r border-gray-300 truncate ${isOverlap ? 'font-semibold text-red-600' : isMonthAdjacent ? 'font-semibold text-amber-600' : hasErrors ? 'text-gray-400' : 'text-gray-700'}`}
                          >
                            {formatDateCell(
                              row.student._RAW_INICI_PRACTIQUES,
                              row.student['INICI PRACTIQUES']
                            )}
                          </td>

                          <td
                            className={`p-1.5 ${borderB} border-r border-gray-300 truncate ${isOverlap ? 'font-semibold text-red-600' : isMonthAdjacent ? 'font-semibold text-amber-600' : hasErrors ? 'text-gray-400' : 'text-gray-700'}`}
                          >
                            {formatDateCell(
                              row.student._RAW_FI_PRACTIQUES,
                              row.student['FI PRACTIQUES']
                            )}
                          </td>

                          <td className={`p-1.5 ${borderB} border-gray-300`}>
                            {/* Es mostren TOTS els problemes que afecten la fila alhora (p. ex. un camp incorrecte + un solapament) */}
                            <div className="space-y-1">
                              {/* Ordre de prioritat visual: CAS 1 (solapament) > CAS 2 (mes-adjacent) > errors de camp */}
                              {isOverlap && (
                                <div
                                  className="space-y-0.5"
                                  title={row.duplicateConflicts
                                    ?.map(formatConflictDetail)
                                    .join('\n')}
                                >
                                  <span className="w-full flex items-center gap-1.5 text-xs font-medium text-red-700 leading-tight">
                                    · Alumne amb DNI duplicat i amb període de
                                    pràctiques solapat
                                  </span>

                                  {duplicateSourceMessages(row).map(
                                    (msg, k) => (
                                      <p
                                        key={`overlap-${k}`}
                                        className="flex items-start gap-1.5 pl-3 text-[11px] font-medium leading-snug text-red-600"
                                      >
                                        <span className="shrink-0">
                                          {msg.icon}
                                        </span>
                                        <span>{msg.text}</span>
                                      </p>
                                    )
                                  )}
                                </div>
                              )}

                              {isMonthAdjacent && (
                                <div
                                  className="space-y-0.5"
                                  title={row.duplicateConflicts
                                    ?.map(formatConflictDetail)
                                    .join('\n')}
                                >
                                  <span className="w-full flex items-center gap-1.5 text-xs font-medium text-amber-700 leading-tight">
                                    · Alumne amb DNI duplicat, algun període de
                                    pràctiques acaba i comença en el mateix mes
                                  </span>

                                  {duplicateSourceMessages(row).map(
                                    (msg, k) => (
                                      <p
                                        key={`month-${k}`}
                                        className="flex items-start gap-1.5 pl-3 text-[11px] font-medium leading-snug text-amber-700"
                                      >
                                        <span className="shrink-0">
                                          {msg.icon}
                                        </span>
                                        <span>{msg.text}</span>
                                      </p>
                                    )
                                  )}
                                </div>
                              )}

                              {hasErrors && (
                                <div className="space-y-1">
                                  {row.errors.map((err, j) => (
                                    <span
                                      key={`error-${j}`}
                                      className="w-full flex items-center gap-1.5 text-xs font-medium text-gray-600 leading-tight"
                                    >
                                      · {err}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {!hasErrors && !isOverlap && !isMonthAdjacent && (
                                <span className="w-full flex items-center gap-1.5 text-xs font-medium text-green-700">
                                  · Correcte
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {step === 3 && importResult && (
            <div className="flex flex-col items-center justify-center w-full py-6 space-y-6 grow">
              {/* Icono + resum principal */}
              <div className="flex flex-col items-center gap-3 text-center">
                <span className="flex items-center justify-center w-16 h-16 bg-green-200 rounded-full">
                  <ImCheckmark className="text-green-500" size={32} />
                </span>

                <div>
                  <p className="text-lg font-semibold text-gray-800">
                    Importació completada
                  </p>

                  <p className="text-sm text-gray-500">
                    {importResult.successCount} alumne
                    {importResult.successCount !== 1 ? 's' : ''} importat
                    {importResult.successCount !== 1 ? 's' : ''} correctament
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Botons: Tancar / Enrere / Analitzar / Importar / Finalitzar */}
          <div className="flex flex-row items-center justify-between mt-5 space-x-2">
            <button
              type="button"
              onClick={() => onClose()}
              disabled={loading}
              className={`${step === 3 && 'invisible'} flex items-center justify-center w-fit px-3 py-2 space-x-1 text-sm text-white transition-colors duration-200 bg-gray-500 border border-gray-500 rounded-sm enabled:hover:bg-white enabled:hover:text-gray-500 disabled:opacity-30`}
            >
              <BiX size={20} />
              <span className="hidden md:flex">Tancar</span>
            </button>

            <div className="flex flex-row space-x-2 sm:items-center sm:justify-between sm:gap-x-4">
              {step === 2 && (
                <button
                  type="button"
                  onClick={() => {
                    setStep(1)
                    setPreviewRows([])
                    setFile(null)
                  }}
                  disabled={loading}
                  className="flex items-center justify-center px-3 py-2 space-x-2 text-sm text-white transition-colors duration-200 bg-gray-500 border border-gray-500 rounded-sm w-fit enabled:hover:bg-white enabled:hover:text-gray-500 disabled:opacity-30"
                >
                  <TbArrowMoveLeft size={20} />
                  <span className="hidden md:flex">Enrere</span>
                </button>
              )}

              {step === 1 && (
                <button
                  type="button"
                  onClick={handleGoToPreview}
                  disabled={!file || loading}
                  className="flex items-center justify-center gap-2 px-3 py-2 text-sm text-white bg-indigo-800 border border-indigo-900 hover:bg-indigo-900 disabled:bg-indigo-800/50 disabled:cursor-not-allowed disabled:border-transparent"
                >
                  {loading ? (
                    <ImSpinner10 size={20} className="animate-spin" />
                  ) : (
                    <>
                      <span className="hidden md:flex">Analitzar fitxer</span>
                      <TbArrowMoveRight size={20} />
                    </>
                  )}
                </button>
              )}

              {step === 2 &&
                (() => {
                  const blockingCount = previewRows.filter(
                    (r) =>
                      r.category === 'overlap' ||
                      r.category === 'monthAdjacent' ||
                      r.errors.length > 0
                  ).length
                  const isBlocked = blockingCount > 0

                  return (
                    <div className="flex flex-col items-end gap-1">
                      {isBlocked ? (
                        <span className="flex items-center gap-1 text-xs font-medium text-red-700">
                          <BiLockAlt size={14} className="shrink-0" />
                          <span className="hidden md:flex">
                            Cal corregir {blockingCount} fil
                            {blockingCount !== 1 ? 'es' : 'a'} per activar la
                            importació
                          </span>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={handleGoToResult}
                          disabled={loading}
                          className="flex items-center justify-center gap-2 px-3 py-2 text-sm text-white transition-colors duration-200 bg-indigo-800 border border-indigo-900 hover:bg-indigo-900 disabled:bg-indigo-800/50 disabled:cursor-not-allowed disabled:border-transparent"
                        >
                          {loading ? (
                            <ImSpinner10 size={20} className="animate-spin" />
                          ) : (
                            <>
                              <ImUpload size={20} />
                              <span className="hidden md:flex">
                                Importar ({previewRows?.length})
                              </span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )
                })()}

              {step === 3 && (
                <button
                  type="button"
                  onClick={() => onClose()}
                  className="flex items-center justify-center gap-2 px-3 py-2 text-sm text-white bg-indigo-800 border border-indigo-900 hover:bg-indigo-900 disabled:bg-indigo-800/50 disabled:cursor-not-allowed disabled:border-transparent"
                >
                  <ImCheckmark size={20} />
                  <span className="hidden md:flex">Finalitzar</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

ImportAlumnesModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  handleAnalyzeExcel: PropTypes.func.isRequired,
  handleConfirmImport: PropTypes.func.isRequired
}
